'use client'

import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { PublicationDraft } from '@/lib/publications/types'

export type OverlayTone = 'black' | 'white' | 'none'

export interface OfferPackProps {
  draft: PublicationDraft
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
}

export interface OfferVisualDefaults {
  overlay?: OverlayTone
  opacity?: number
  blur?: boolean
}

export interface OfferCopyFallback {
  eyebrow?: string
  headline?: string
  subheadline?: string
  body?: string
  cta?: string
  offer?: string
  proof?: string
  urgency?: string
  price?: string
  oldPrice?: string
  bullets?: string[]
}

export function clampIndex(index: number | undefined, total: number) {
  const safeTotal = Math.max(total, 1)
  const safeIndex = Number.isFinite(index) ? Number(index) : 0
  return Math.min(Math.max(safeIndex, 0), safeTotal - 1)
}

export function getDesign(draft: PublicationDraft, fallbackAccent: string) {
  const design = ((draft as any).design || {}) as Record<string, any>

  return {
    accent: design.accent || fallbackAccent,
    accentSoft: design.accentSoft || design.accent || fallbackAccent,
    accentDeep: design.accentDeep || design.accent || fallbackAccent,
    text: design.text,
    textMuted: design.textMuted,
    surface: design.surface,
    border: design.border,
  }
}

export function getOfferVisual(
  draft: PublicationDraft,
  activeIndex: number,
  fallbackSrc: string,
  defaults?: OfferVisualDefaults
) {
  const visuals = Array.isArray((draft as any).slideVisuals) ? (draft as any).slideVisuals : []
  const visual = visuals[activeIndex] || {}

  return {
    src: visual.backgroundImage || fallbackSrc,
    blur: visual.blurEnabled ?? defaults?.blur ?? false,
    overlay: (visual.overlayTone || defaults?.overlay || 'black') as OverlayTone,
    opacity: visual.overlayOpacity ?? defaults?.opacity ?? 0.35,
  }
}

export function getOfferCopy(
  draft: PublicationDraft,
  activeIndex: number,
  fallback: OfferCopyFallback
) {
  const slides = Array.isArray((draft as any).slides) ? (draft as any).slides : []
  const slide = slides[activeIndex] || {}
  const root = draft as any

  const bullets = Array.isArray(slide.bullets)
    ? slide.bullets
    : Array.isArray(slide.items)
      ? slide.items
      : Array.isArray(root.bullets)
        ? root.bullets
        : fallback.bullets || []

  return {
    eyebrow: slide.eyebrow || slide.kicker || fallback.eyebrow || '',
    headline: slide.headline || slide.title || root.headline || root.title || fallback.headline || '',
    subheadline: slide.subheadline || slide.subtitle || root.subheadline || root.subtitle || fallback.subheadline || '',
    body: slide.body || slide.description || fallback.body || '',
    cta: slide.cta || root.cta || fallback.cta || 'Quiero empezar',
    offer: slide.offer || root.offer || fallback.offer || '',
    proof: slide.proof || root.proof || fallback.proof || '',
    urgency: slide.urgency || root.urgency || fallback.urgency || '',
    price: slide.price || root.price || fallback.price || '',
    oldPrice: slide.oldPrice || root.oldPrice || fallback.oldPrice || '',
    bullets,
  }
}

export function OfferFrame({
  activeIndex = 0,
  total,
  onActiveIndexChange,
  children,
  className = '',
  text = '#111111',
  borderRadius = 34,
}: {
  activeIndex?: number
  total: number
  onActiveIndexChange?: (index: number) => void
  children: ReactNode
  className?: string
  text?: string
  borderRadius?: number
}) {
  const safeTotal = Math.max(total, 1)
  const prev = () => onActiveIndexChange?.((activeIndex - 1 + safeTotal) % safeTotal)
  const next = () => onActiveIndexChange?.((activeIndex + 1) % safeTotal)

  const VIRTUAL_WIDTH = 533
  const VIRTUAL_HEIGHT = 711

  return (
    <div className="space-y-3">
      <div
        className={`relative w-full overflow-hidden ${className}`}
        style={{
          aspectRatio: `${VIRTUAL_WIDTH} / ${VIRTUAL_HEIGHT}`,
          borderRadius,
          boxShadow: '0 28px 90px rgba(0,0,0,0.16)',
          containerType: 'inline-size',
        }}
      >
        <div
          className="absolute left-0 top-0 overflow-hidden"
          style={{
            width: VIRTUAL_WIDTH,
            height: VIRTUAL_HEIGHT,
            transformOrigin: 'top left',
            transform: `scale(calc(100cqw / ${VIRTUAL_WIDTH}px))`,
          }}
        >
        {children}

        {safeTotal > 1 ? (
          <div className="absolute right-5 top-5 z-50 flex gap-2">
            <button
              type="button"
              onClick={prev}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/35 text-white backdrop-blur transition hover:scale-105"
              aria-label="Slide anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={next}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/35 text-white backdrop-blur transition hover:scale-105"
              aria-label="Siguiente slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {safeTotal > 1 ? (
          <div className="absolute bottom-5 left-6 z-50 flex items-center gap-1.5">
            {Array.from({ length: safeTotal }).map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onActiveIndexChange?.(idx)}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: idx === activeIndex ? 24 : 7,
                  background: text,
                  opacity: idx === activeIndex ? 0.95 : 0.28,
                }}
                aria-label={`Ir al slide ${idx + 1}`}
              />
            ))}
          </div>
        ) : null}

        <div className="absolute bottom-5 right-6 z-50 text-[10px] font-bold text-white/65">
          {activeIndex + 1}/{safeTotal}
        </div>
      </div>
      </div>
    </div>
  )
}

export function FullBleedImage({
  src,
  blur,
  overlay = 'black',
  opacity = 0.35,
}: {
  src?: string | null
  blur?: boolean
  overlay?: OverlayTone
  opacity?: number
}) {
  return (
    <>
      {src ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            filter: blur ? 'blur(14px)' : 'none',
            transform: blur ? 'scale(1.08)' : 'scale(1)',
          }}
        />
      ) : null}

      {overlay !== 'none' ? (
        <div
          className="absolute inset-0"
          style={{
            background: overlay === 'black' ? '#000000' : '#ffffff',
            opacity,
          }}
        />
      ) : null}
    </>
  )
}

export function SafeText({
  children,
  className = '',
  style,
}: {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div className={className} style={{ overflowWrap: 'anywhere', ...style }}>
      {children}
    </div>
  )
}

export function OfferBadge({
  children,
  color,
  background = 'rgba(255,255,255,0.16)',
}: {
  children: ReactNode
  color: string
  background?: string
}) {
  if (!children) return null

  return (
    <div
      className="inline-flex w-fit rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]"
      style={{ color, background }}
    >
      {children}
    </div>
  )
}
